import { ReactionDto } from './reaction-dto';
import { EventStatus, IContent, MatrixEvent, MsgType as MatrixMsgType } from 'matrix-js-sdk';
import { DisplayItem, DisplayItemType } from './display-item';
import { ChatMessage } from '../../../model/chat-message';
import { Observable } from 'rxjs';

/**
 * Display Item, welches die Nachrichten im Chatverlauf kapselt
 * Begründung: keine AFO, weil ...
 */
export class DisplayMessage extends DisplayItem {
  constructor(
    protected readonly message: ChatMessage,
    private readonly _isPinned = false,
    private readonly _isReadByAll$?: Observable<boolean>,
    private readonly _inReplyTo?: DisplayMessage
  ) {
    super(message.first.getId());
  }

  override get type(): DisplayItemType {
    return DisplayItemType.MESSAGE;
  }

  get eventId(): string | undefined {
    return this.event.getId();
  }

  /**
   * Event-ID der Original-Nachricht (löst m.replace-Edits auf). Für Aktionen, die die gesamte
   * Nachricht betreffen (Löschen/Redact), muss das Original adressiert werden – sonst würde nur
   * das Edit-Event redacted und die Nachricht fiele auf den ursprünglichen Text zurück (TIME-4164).
   * `eventId`/`first` ist nicht zwingend das Original (z.B. wenn nur das Edit-Event geladen ist).
   */
  get originalEventId(): string | undefined {
    return this.message.originalId;
  }

  get content(): IContent {
    return this.event.getContent();
  }

  get sender(): string {
    return this.event.sender?.rawDisplayName ?? 'Unbekannt';
  }

  get latAndLong(): { lat: number; long: number } | null {
    return this.parseGeoUri(this.event.getContent()['geo_uri']);
  }

  get senderId(): string {
    return this.event.sender?.userId ?? 'Unbekannt';
  }

  get body(): string {
    return this.content?.['body'] ?? 'leer';
  }

  get formattedBody(): string {
    return this.content?.['formatted_body'] ?? this.body;
  }

  get date(): Date | null {
    return this.event.getDate();
  }

  /** @deprecated */
  get status(): EventStatus | null {
    return this.event.status ?? EventStatus.SENT;
  }

  get mentionsArray(): string[] {
    return this.content?.['m.mentions']?.user_ids ?? [];
  }

  get reactionsArray(): ReactionDto[] {
    return this.groupReactionsByEmoji(this.message.reactions);
  }

  get isEdited(): boolean {
    return this.message.isEdited();
  }

  get isRedacted(): boolean {
    return this.message.isRedacted();
  }

  get displayStatus(): EventStatus | null | undefined {
    // TODO An zu ermittelnde Anforderungen anpassen
    // ...
    return this.status;
  }

  private get event(): MatrixEvent {
    return this.message.first;
  }

  get inReplyTo(): DisplayMessage | undefined {
    return this._inReplyTo;
  }

  get isPinned(): boolean {
    return this._isPinned;
  }

  get chatMessage(): ChatMessage {
    return this.message;
  }

  get isReadByAll$(): Observable<boolean> | undefined {
    return this._isReadByAll$;
  }

  private groupReactionsByEmoji(events: MatrixEvent[]): ReactionDto[] {
    const grouped = new Map<string, MatrixEvent[]>();
    for (const event of events) {
      const emoji = event.getContent()?.['m.relates_to']?.['key'];
      if (emoji && event.getSender() && event.getId()) {
        (grouped.get(emoji) ?? grouped.set(emoji, []).get(emoji)!).push(event);
      }
    }
    return [...grouped].map(([emoji, events]) => ({ emoji, events }));
  }

  private parseGeoUri(uri: string): { lat: number; long: number } | null {
    const matcher = /^geo:([0-9.-]+),([0-9.-]+)/;
    const match = matcher.exec(uri);
    if (!match) return null;
    return { lat: Number.parseFloat(match[1]), long: Number.parseFloat(match[2]) };
  }
}

export const MsgType = {
  Text: MatrixMsgType.Text,
  Emote: MatrixMsgType.Emote,
  Notice: MatrixMsgType.Notice,
  Image: MatrixMsgType.Image,
  File: MatrixMsgType.File,
  Audio: MatrixMsgType.Audio,
  Location: MatrixMsgType.Location,
  Video: MatrixMsgType.Video,
  KeyVerificationRequest: MatrixMsgType.KeyVerificationRequest,
  BadEncrypted: 'm.bad.encrypted',
  Sticker: 'm.sticker'
} as const;

// Erzeugt den Typ aus den Werten des Objekts
export type MsgType = (typeof MsgType)[keyof typeof MsgType];
